// 📄 components/credenciales/CredentialCard.tsx
import React, { useState } from 'react';
import { KeyRound, ExternalLink, Copy, Pencil, Trash2, Check } from 'lucide-react';
import { CredentialAmbiente, CredentialMetadata } from '../../services/credentialService.ts';

interface CredentialCardProps {
  credential: CredentialMetadata;
  isOwner: boolean;
  onEdit: () => void;
  onDelete: () => void;
  // Ver / pedir la contraseña y su flujo de aprobación (FlujoAprobacionCredencial.tsx).
  // Desde R5 (plan-revelado-credenciales) la tarjeta ya no lee la contraseña:
  // quien la creó la ve con re-autenticación, el equipo la pide con aprobación,
  // y todo queda en el historial.
  onContrasena: () => void;
}

const AMBIENTE_CLASSES: Record<CredentialAmbiente, string> = {
  DEV: 'bg-blue-500/15 text-blue-400',
  QAS: 'bg-amber-500/15 text-amber-400',
  PRD: 'bg-emerald-500/15 text-emerald-400',
  OTRO: 'bg-gray-500/15 text-gray-400'
};

export default function CredentialCard({ credential, isOwner, onEdit, onDelete, onContrasena }: CredentialCardProps) {
  const [copied, setCopied] = useState(false);

  const handleCopyUsuario = async () => {
    await navigator.clipboard.writeText(credential.usuario);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <KeyRound className="w-4 h-4 text-blue-400 flex-shrink-0" />
          <h4 className="text-sm font-semibold text-white truncate" title={credential.nombre}>{credential.nombre}</h4>
        </div>
        <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 ${AMBIENTE_CLASSES[credential.ambiente]}`}>
          {credential.ambiente}
        </span>
      </div>

      <span className={`self-start text-[11px] font-medium px-2 py-0.5 rounded-full ${credential.compartida ? 'bg-blue-500/15 text-blue-400' : 'bg-white/10 text-gray-400'}`}>
        {credential.compartida ? 'Compartida' : 'Privada'}
      </span>

      {credential.url && (
        <a
          href={credential.url}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-1.5 text-xs text-blue-400 hover:underline truncate"
        >
          <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">{credential.url}</span>
        </a>
      )}

      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="text-gray-400 truncate" title={credential.usuario}>{credential.usuario}</span>
        <button
          type="button"
          onClick={handleCopyUsuario}
          title="Copiar usuario"
          className="flex-shrink-0 p-1 rounded hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>

      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="text-gray-400 font-mono truncate">••••••••</span>
        <button
          type="button"
          onClick={onContrasena}
          title={isOwner ? 'Ver la contraseña (te pide tu contraseña de OneHub y queda en el historial)' : 'Pedir la contraseña (necesita aprobación)'}
          className="flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
        >
          <KeyRound className="w-3.5 h-3.5" /> {isOwner ? 'Ver' : 'Pedir'}
        </button>
      </div>

      {credential.nota && (
        <p className="text-xs text-gray-400 pt-2 border-t border-white/10">{credential.nota}</p>
      )}

      <div className="flex items-center justify-between pt-1">
        <div>
          {!isOwner && (
            <p className="text-[11px] text-gray-500">Creado por {credential.creadoPorNombre || 'un miembro del equipo'}</p>
          )}
        </div>
        <div className="flex items-center gap-1">
          {isOwner && (
            <>
              <button type="button" onClick={onEdit} title="Editar" className="p-1.5 rounded hover:bg-white/10 text-gray-400 hover:text-white transition-colors">
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={onDelete} title="Eliminar" className="p-1.5 rounded hover:bg-white/10 text-gray-400 hover:text-red-400 transition-colors">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
